import {
  Card,
  Checkbox,
  Row,
  Col,
  Typography,
  Collapse,
  Spin,
  Form,
  Tabs,
} from "antd";
import { PlusOutlined, SearchOutlined, SendOutlined } from "@ant-design/icons";
import CustomButton from "../../components/CustomButton/Button";
import BackButton from "../../components/CustomButton/BackButton";
import PrimaryButton from "../../components/CustomButton/PrimaryButton";
import CustomSelect from "../../components/CustomSelect/CustomSelect";
import CustomInput from "../../components/CustomInput/Input";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState, useRef } from "react";
import { capitalizeWords } from "../../helpers/helpers";
import {
  getJvEnquiryOverlapQuestionIds,
  shouldSkipJvEnquiryOverlapQuestion,
  isDeprecatedJvTenureQuestion,
  dedupeQuestionIdsByTitle,
  buildDefaultJvKycQuestionSelection,
  collectJvDealQuestionIds,
  isJvDealCategoryName,
  JV_DEFAULT_KYC_QUESTION_IDS,
} from "../../helpers/jvQuestionnaire";
import { useSelector, useDispatch } from "react-redux";
import {
  getQuestionDropdowns,
  getFilteredQuestions,
  getMappedQuestions,
  sendQuestionnaire,
  resetState,
  getPredefinedThreads,
  getQuestionnaireThreads,
} from "../../redux/slices/QuestionBankSlice";
import { dealAutofill } from "../../redux/slices/jointVenturesSlice";
import CustomModal from "../../components/CustomModal/CustomModal";
import {
  MODAL_WIDTHS,
  ENQUIRY_SUCCESS_MESSAGES,
} from "../../constants/constants";
import AddQuestionForm from "./AddQuestionForm";
import { message } from "antd";

import "./EnqMngt.scss";

const { Title, Text } = Typography;
const { Panel } = Collapse;

const mandatoryQuestionIds = [...JV_DEFAULT_KYC_QUESTION_IDS];

const SendQuestionnaireForm = () => {
  const {
    dropdownData,
    filteredQuestions,
    mappedQuestions,
    loading,
    sendQuestionnaireLoading,
    sendQuestionnaireError,
    predefinedThreads,
    questionnaireThreads: storeQuestionnaireThreads,
  } = useSelector((state) => state.questionBank);
  const { dealAutofillList } = useSelector((state) => state.jointVentures);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { orgId } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const { isCreditAssessment, dealId: stateDealId, org, orgData, fromDeals, questionnaireThreads, dealCategoryId: stateDealCategoryId } = location.state || {};
  const isFromDeals = Boolean(
    fromDeals ||
      stateDealId ||
      searchParams.get("fromDeals") === "true" ||
      searchParams.get("dealId")
  );

  const dealId = stateDealId || searchParams.get("dealId") || undefined;

  const dealCategoryId =
    org?.dealCategoryId ||
    orgData?.dealCategoryId ||
    stateDealCategoryId ||
    searchParams.get("dealCategoryId") ||
    storeQuestionnaireThreads?.orgData?.dealCategoryId;
  const entityTypeId =
    org?.entityTypeId ||
    org?.businessTypeId ||
    orgData?.entityTypeId ||
    orgData?.businessTypeId;
  const isJvOrg = dealCategoryId === "DCT1000002";
  const isRepeatJvSend = isJvOrg && (questionnaireThreads?.length > 0);
  // First-deal overlap skip is only for enquiry onboarding, never for Deals Management questionnaires
  const isJvFirstDeal = isJvOrg && !isFromDeals && !!dealAutofillList?.isFirstDeal;

  const jvEnquiryBlockedQuestionIds = useMemo(() => {
    if (!isJvFirstDeal) return new Set();
    // Only skip overlap questions when enquiry autofill already has that value
    return getJvEnquiryOverlapQuestionIds(filteredQuestions, dealAutofillList);
  }, [isJvFirstDeal, filteredQuestions, dealAutofillList]);

  const alreadySentQuestionIds = useMemo(() => {
    const ids = new Set(mappedQuestions?.sentQuestionIds || []);
    (mappedQuestions?.questions || []).forEach((question) => {
      if (question?.isAlreadySent && question.questionId) {
        ids.add(question.questionId);
      }
    });
    (filteredQuestions || []).forEach((category) => {
      category.questions?.forEach((question) => {
        if (question?.isAlreadySent && question.questionId) {
          ids.add(question.questionId);
        }
      });
    });
    return ids;
  }, [filteredQuestions, mappedQuestions?.questions, mappedQuestions?.sentQuestionIds]);

  const isQuestionBlocked = (questionId, questionTitle) =>
    jvEnquiryBlockedQuestionIds.has(questionId) ||
    (isJvFirstDeal &&
      shouldSkipJvEnquiryOverlapQuestion(questionTitle, dealAutofillList)) ||
    (isJvOrg && isDeprecatedJvTenureQuestion(questionTitle));

  const isMandatoryLocked = (questionId) =>
    dealId && mandatoryQuestionIds.includes(questionId);

  const [selectedQuestions, setSelectedQuestions] = useState({});
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [questionFormModal, setQuestionFormModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [threadName, setThreadName] = useState("");
  const [selectedThreadQuestionIds, setSelectedThreadQuestionIds] = useState([]);
  const defaultsAppliedRef = useRef(false);

  useEffect(() => {
    defaultsAppliedRef.current = false;
  }, [orgId, dealId]);

  useEffect(() => {
    dispatch(getQuestionDropdowns());
    dispatch(getPredefinedThreads({ orgId, dealId, dealCategoryId }));
  }, [dispatch, orgId, dealId, dealCategoryId]);

  // Recover dealCategoryId after refresh / lost navigation state so JV sends are not tagged as IF.
  useEffect(() => {
    if (!orgId || dealCategoryId) return;
    dispatch(getQuestionnaireThreads({ orgId, dealId }));
  }, [dispatch, orgId, dealId, dealCategoryId]);

  useEffect(() => {
    if (!orgId || !isJvOrg || isFromDeals) return;
    dispatch(dealAutofill(orgId));
  }, [dispatch, orgId, isJvOrg, isFromDeals]);

  useEffect(() => {
    if (!dealCategoryId || !entityTypeId) return;

    const params = {
      orgId,
      dealId,
      dealCategoryId,
      entityTypeId,
    };

    if (selectedCategory !== "all") params.categoryId = selectedCategory;
    dispatch(getFilteredQuestions(params));
  }, [selectedCategory, dispatch, orgId, dealId, dealCategoryId, entityTypeId]);

  const questionsByCategory = useMemo(() => {
    const categoryNameById = new Map();
    (filteredQuestions || []).forEach((category) => {
      categoryNameById.set(category.categoryId, category.categoryName);
    });
    (dropdownData?.categories || []).forEach((category) => {
      categoryNameById.set(category.categoryId, category.categoryName);
    });

    const mapped = mappedQuestions?.questions || [];
    if (mapped.length) {
      const grouped = new Map();
      mapped.forEach((question) => {
        if (isDeprecatedJvTenureQuestion(question.questionTitle)) return;
        const categoryId = question.categoryId || "UNCATEGORIZED";
        if (!grouped.has(categoryId)) {
          grouped.set(categoryId, {
            categoryId,
            categoryName:
              categoryNameById.get(categoryId) ||
              question.categoryName ||
              "Compliance",
            questions: [],
          });
        }
        grouped.get(categoryId).questions.push({
          questionId: question.questionId,
          questionTitle: question.questionTitle,
          isAlreadySent: Boolean(question.isAlreadySent),
        });
      });
      return Array.from(grouped.values()).filter(
        (category) => category.questions.length > 0,
      );
    }

    return (filteredQuestions || []).map((category) => ({
      ...category,
      questions: (category.questions || []).filter(
        (question) => !isDeprecatedJvTenureQuestion(question.questionTitle),
      ),
    })).filter((category) => (category.questions || []).length > 0);
  }, [
    dropdownData?.categories,
    filteredQuestions,
    mappedQuestions?.questions,
  ]);

  // JV Deal category questions stay selectable on every send (re-answer → approve → deal upload).
  const jvDealResendableQuestionIds = useMemo(() => {
    if (!isJvOrg) return new Set();
    return collectJvDealQuestionIds(questionsByCategory, []);
  }, [isJvOrg, questionsByCategory]);

  const isAlreadySentQuestion = (questionId) => {
    if (jvDealResendableQuestionIds.has(questionId)) return false;
    return alreadySentQuestionIds.has(questionId);
  };

  const isThreadWhollyLocked = (thread) =>
    !isJvOrg && Boolean(thread?.isThreadAlreadySent);

  const isCheckboxDisabled = (questionId, questionTitle) =>
    isMandatoryLocked(questionId) ||
    isQuestionBlocked(questionId, questionTitle) ||
    isAlreadySentQuestion(questionId);

  const getQuestionStatusSuffix = (questionId, questionTitle) => {
    if (isAlreadySentQuestion(questionId)) return " (already sent)";
    if (isQuestionBlocked(questionId, questionTitle)) {
      return " (from enquiry — not required)";
    }
    return "";
  };

  const visibleQuestionsByCategory = useMemo(() => {
    if (selectedCategory === "all") return questionsByCategory;
    return questionsByCategory.filter(
      (category) => category.categoryId === selectedCategory,
    );
  }, [questionsByCategory, selectedCategory]);

  const sendableQuestionIdSet = useMemo(() => {
    const ids = new Set();
    (questionsByCategory || []).forEach((category) => {
      category.questions?.forEach((question) => {
        if (
          !isAlreadySentQuestion(question.questionId) ||
          isJvDealCategoryName(category.categoryName)
        ) {
          ids.add(question.questionId);
        }
      });
    });
    return ids;
  }, [alreadySentQuestionIds, jvDealResendableQuestionIds, questionsByCategory]);

  const getThreadQuestionDetails = (thread) => {
    if (thread?.questions?.length) {
      return thread.questions;
    }
    return (thread?.questionIds || []).map((questionId) => ({
      questionId,
      questionTitle: questionId,
    }));
  };

  useEffect(() => {
    if (!entityTypeId || !dealCategoryId) return;

    dispatch(
      getMappedQuestions({
        entityTypeId,
        dealCategoryId,
        orgId: orgId ? orgId : orgData?.orgId,
        dealId,
      }),
    );
  }, [
    dispatch,
    dealCategoryId,
    entityTypeId,
    orgId,
    dealId,
    orgData?.orgId,
  ]);

  useEffect(() => {
    return () => {
      dispatch(resetState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!mappedQuestions?.questions?.length || defaultsAppliedRef.current) return;

    setSelectedQuestions((prev) => {
      const hasExistingSelection = Object.values(prev).some((ids) => ids.length > 0);
      if (hasExistingSelection) {
        defaultsAppliedRef.current = true;
        return prev;
      }

      if (isRepeatJvSend) {
        defaultsAppliedRef.current = true;
        return buildDefaultJvKycQuestionSelection(
          mappedQuestions.questions,
          mandatoryQuestionIds,
        );
      }

      if (!dealId) {
        return prev;
      }

      const updated = { ...prev };
      mappedQuestions.questions.forEach((q) => {
        if (mandatoryQuestionIds.includes(q.questionId)) {
          const catId = q.categoryId;
          if (!catId) return;
          if (!updated[catId]) {
            updated[catId] = [];
          }
          if (!updated[catId].includes(q.questionId)) {
            updated[catId] = [...updated[catId], q.questionId];
          }
        }
      });
      defaultsAppliedRef.current = true;
      return updated;
    });
  }, [dealId, mappedQuestions, isRepeatJvSend]);

  useEffect(() => {
    if (!isJvFirstDeal || jvEnquiryBlockedQuestionIds.size === 0) return;

    setSelectedQuestions((prev) => {
      let changed = false;
      const updated = Object.fromEntries(
        Object.entries(prev).map(([categoryId, ids]) => {
          const filtered = ids.filter(
            (id) => !jvEnquiryBlockedQuestionIds.has(id),
          );
          if (filtered.length !== ids.length) changed = true;
          return [categoryId, filtered];
        }),
      );
      return changed ? updated : prev;
    });
  }, [isJvFirstDeal, jvEnquiryBlockedQuestionIds]);

  // ✅ Checkbox handler (using questionId)
  const handleCheckboxChange = (categoryId, questionId, questionTitle) => {
    if (isCheckboxDisabled(questionId, questionTitle)) {
      return;
    }

    setSelectedQuestions((prev) => {
      const categorySelections = prev[categoryId] || [];
      if (categorySelections.includes(questionId)) {
        return {
          ...prev,
          [categoryId]: categorySelections.filter((id) => id !== questionId),
        };
      } else {
        return {
          ...prev,
          [categoryId]: [...categorySelections, questionId],
        };
      }
    });
  };

  const handleCategoryCheckboxChange = (category, isChecked) => {
    const categoryId = category?.categoryId;
    const categoryQuestionIds =
      category?.questions?.map((q) => q.questionId) || [];

    if (!categoryId || categoryQuestionIds.length === 0) return;

    setSelectedQuestions((prev) => {
      const currentSelections = prev[categoryId] || [];
      const mandatorySelections = dealId
        ? categoryQuestionIds.filter((id) => mandatoryQuestionIds.includes(id))
        : [];
      const selectableQuestionIds = category.questions
        ?.filter(
          (q) =>
            !isQuestionBlocked(q.questionId, q.questionTitle) &&
            !isAlreadySentQuestion(q.questionId),
        )
        .map((q) => q.questionId) || [];

      if (isChecked) {
        return {
          ...prev,
          [categoryId]: Array.from(
            new Set([...currentSelections, ...selectableQuestionIds]),
          ),
        };
      }

      if (mandatorySelections.length > 0) {
        return {
          ...prev,
          [categoryId]: mandatorySelections,
        };
      }

      const updatedSelections = { ...prev };
      delete updatedSelections[categoryId];
      return updatedSelections;
    });
  };

  const getTotalSelected = () => {
    return Object.values(selectedQuestions).flat().length;
  };

  const maybeDefaultThreadName = (thread) => {
    if (thread && !threadName.trim()) {
      setThreadName(thread.threadName);
    }
  };

  const handleThreadQuestionToggle = (thread, questionId, questionTitle) => {
    if (isThreadWhollyLocked(thread)) return;
    if (isThreadQuestionAlreadySent(thread, questionId)) return;
    if (isQuestionBlocked(questionId, questionTitle)) return;

    setSelectedThreadQuestionIds((prev) => {
      if (prev.includes(questionId)) {
        return prev.filter((id) => id !== questionId);
      }
      maybeDefaultThreadName(thread);
      return [...prev, questionId];
    });
  };

  const handleThreadSelectAll = (thread) => {
    if (isThreadWhollyLocked(thread)) return;

    const sendableIds = getThreadSendableQuestionIds(thread);
    if (sendableIds.length === 0) {
      message.warning(
        "All questions in this thread were already sent to this organisation.",
      );
      return;
    }

    const allSelected = sendableIds.every((id) =>
      selectedThreadQuestionIds.includes(id),
    );

    setSelectedThreadQuestionIds((prev) => {
      if (allSelected) {
        return prev.filter((id) => !sendableIds.includes(id));
      }
      maybeDefaultThreadName(thread);
      return Array.from(new Set([...prev, ...sendableIds]));
    });
  };

  const getFilteredQuestionsWithSearch = () => {
    if (!searchQuery.trim()) {
      return visibleQuestionsByCategory;
    }

    const lowerSearchQuery = searchQuery.toLowerCase();

    return visibleQuestionsByCategory
      ?.map((category) => ({
        ...category,
        questions: category.questions?.filter((question) =>
          question.questionTitle.toLowerCase().includes(lowerSearchQuery),
        ),
      }))
      .filter((category) => category.questions?.length > 0);
  };

  const getMergedQuestionsWithSearch = () => {
    const filteredCategories = getFilteredQuestionsWithSearch() || [];

    const mergedCategories = filteredCategories.map((category) => ({
      ...category,
      questions: category.questions ? [...category.questions] : [],
    }));

    return mergedCategories;
  };

  const getMergedQuestionsWithoutSearch = () => {
    const filteredCategories = visibleQuestionsByCategory || [];

    const mergedCategories = filteredCategories.map((category) => ({
      ...category,
      questions: category.questions ? [...category.questions] : [],
    }));

    return mergedCategories;
  };

  const filterSendableQuestionIds = (questionIds) =>
    questionIds.filter(
      (id) =>
        !jvEnquiryBlockedQuestionIds.has(id) && !isAlreadySentQuestion(id),
    );

  const isThreadQuestionAlreadySent = (thread, questionId) => {
    if (isThreadWhollyLocked(thread)) return true;
    if (jvDealResendableQuestionIds.has(questionId)) return false;
    return !sendableQuestionIdSet.has(questionId);
  };

  const getThreadSendableQuestionIds = (thread) => {
    if (isThreadWhollyLocked(thread)) return [];
    const questionIds = thread?.questionIds || [];
    // JV: allow re-send of any thread question that is still in the bank / JV Deal set
    const candidates = questionIds.filter(
      (questionId) =>
        sendableQuestionIdSet.has(questionId) ||
        jvDealResendableQuestionIds.has(questionId),
    );
    return filterSendableQuestionIds(candidates);
  };

  const getUniqueSelectedCount = () => {
    const regularQuestionIds = filterSendableQuestionIds(
      Object.entries(selectedQuestions).flatMap(([, ids]) => ids),
    );
    const threadQuestionIds = filterSendableQuestionIds(
      selectedThreadQuestionIds.filter((id) => sendableQuestionIdSet.has(id)),
    );
    const uniqueIds = new Set([...regularQuestionIds, ...threadQuestionIds]);
    return uniqueIds.size;
  };

  const handleSendQuestionnaire = async () => {
    // Get all selected items except custom question indices
    const selectedEntries = Object.entries(selectedQuestions);
    const regularQuestionIds = filterSendableQuestionIds(
      selectedEntries.flatMap(([, ids]) => ids),
    );

    const threadQuestionIds = filterSendableQuestionIds(
      selectedThreadQuestionIds.filter((id) => sendableQuestionIdSet.has(id)),
    );

    const mergedQuestionIds = dedupeQuestionIdsByTitle(
      Array.from(new Set([...regularQuestionIds, ...threadQuestionIds])),
      filteredQuestions,
    );

    if (mergedQuestionIds.length === 0) {
      message.error("Please select at least one question.");
      return;
    }

    const resolvedCategoryId =
      dealCategoryId ||
      (mergedQuestionIds.some((id) => jvDealResendableQuestionIds.has(id))
        ? "DCT1000002"
        : null);

    if (!resolvedCategoryId) {
      message.error(
        "Deal category is missing. Open Send Questionnaire again from Enquiry or Deals.",
      );
      return;
    }

    const payload = {
      threadName: threadName.trim(),
      orgId: orgId,
      questionIds: mergedQuestionIds,
      dealCategoryId: resolvedCategoryId,
    };
    if (dealId) {
      payload.dealId = dealId;
    }

    const response = await dispatch(sendQuestionnaire(payload));

    if (response?.payload?.statusCode === 201) {
      const sentDealId = response?.payload?.response?.dealId || dealId;
      const successMessage = sentDealId
        ? ENQUIRY_SUCCESS_MESSAGES.QUESTIONNAIRE_SENT_WITH_DEAL.replace(
            "{dealId}",
            sentDealId,
          )
        : ENQUIRY_SUCCESS_MESSAGES.QUESTIONNAIRE_SENT;
      message.success(successMessage);
      navigate(`/enquiry-management/send-questionnaire/${orgId}`, {
        state: {
          isCreditAssessment,
          fromDeals: Boolean(isFromDeals || dealId),
          dealId: dealId || undefined,
          dealCategoryId: resolvedCategoryId,
          org: org || orgData || storeQuestionnaireThreads?.orgData,
          orgData: orgData || storeQuestionnaireThreads?.orgData,
          questionnaireThreads:
            questionnaireThreads || storeQuestionnaireThreads?.threads,
        },
      });
    }
  };

  useEffect(() => {
    if (sendQuestionnaireError) {
      message.error(sendQuestionnaireError, 5);
    }
  }, [sendQuestionnaireError]);

  return (
    <div className="questionnaire-page">
      <BackButton onClick={() => navigate(-1)}>
        ← Back to Business Details
      </BackButton>

      <Title level={3}>Send Questionnaire</Title>

      <Card className="question-card">
        {/* ✅ Thread Name Input */}
        <Form width="10%" layout="vertical" style={{ marginBottom: "20px" }}>
          <Col span={12}>
            <CustomInput
              label="Thread Name"
              placeholder="Enter thread name"
              value={threadName}
              isRequired={true}
              onChange={(e) => setThreadName(e.target.value)}
            />
          </Col>
        </Form>

        {/* Tabs */}
        <Tabs
          defaultActiveKey="1"
          style={{ marginBottom: "20px" }}
          items={[
            {
              key: "1",
              label: "Questionnaire",
              children: (
                <div>
                  {/* Header */}
                  <div className="question-header">
                    <div>
                      <Title level={4}>Select Questions</Title>
                      <Text type="secondary">
                        {getTotalSelected()} questions selected from bank
                      </Text>
                    </div>

                    <PrimaryButton
                      icon={<PlusOutlined />}
                      onClick={() => setQuestionFormModal(true)}
                    >
                      Add New Question
                    </PrimaryButton>
                  </div>

                  {/* Filters */}
                  <Row gutter={16} style={{ marginBottom: "20px" }}>
                    <Col flex="4">
                      <CustomInput
                        prefix={<SearchOutlined />}
                        placeholder="Search questions..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </Col>

                    <Col flex="2">
                      <CustomSelect
                        placeholder="Select Category"
                        value={selectedCategory}
                        onChange={(value) => setSelectedCategory(value)}
                        options={[
                          {
                            value: "all",
                            label: "All Categories",
                          },
                          ...(dropdownData?.questionCatogory?.map((cat) => ({
                            value: cat.categoryId,
                            label: capitalizeWords(cat.categoryName),
                          })) || []),
                        ]}
                      />
                    </Col>
                  </Row>

                  {/* Question List */}
                  <Spin spinning={loading}>
                    <Collapse
                      expandIconPosition="end"
                      className="question-collapse"
                      defaultActiveKey={getMergedQuestionsWithSearch()?.map(
                        (category) => category.categoryId,
                      )}
                    >
                      {getMergedQuestionsWithSearch()?.map((category) => {
                        const selectableQuestions =
                          category.questions?.filter(
                            (q) =>
                              !isQuestionBlocked(
                                q.questionId,
                                q.questionTitle,
                              ) && !isAlreadySentQuestion(q.questionId),
                          ) || [];
                        const selectedCount =
                          selectedQuestions[category.categoryId]?.filter((id) =>
                            selectableQuestions.some(
                              (q) => q.questionId === id,
                            ),
                          ).length || 0;

                        const totalCount = selectableQuestions.length;
                        const alreadySentCount =
                          category.questions?.filter((q) =>
                            isAlreadySentQuestion(q.questionId),
                          ).length || 0;

                        const allSelected =
                          totalCount > 0 && selectedCount === totalCount;
                        const isIndeterminate =
                          selectedCount > 0 && selectedCount < totalCount;

                        return (
                          <Panel
                            key={category.categoryId}
                            header={
                              <div className="category-header">
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 12,
                                  }}
                                >
                                  <Checkbox
                                    checked={allSelected}
                                    indeterminate={isIndeterminate}
                                    disabled={totalCount === 0}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) =>
                                      handleCategoryCheckboxChange(
                                        category,
                                        e.target.checked,
                                      )
                                    }
                                  />

                                  <div>
                                    <Title
                                      level={5}
                                      style={{ marginBottom: 0 }}
                                    >
                                      {category.categoryName}
                                    </Title>
                                    <Text type="secondary">
                                      {category.questions?.length || 0} questions
                                      available
                                      {alreadySentCount > 0
                                        ? ` (${alreadySentCount} already sent)`
                                        : ""}
                                      {isJvFirstDeal &&
                                      (category.questions?.length || 0) -
                                        alreadySentCount >
                                        totalCount
                                        ? ` (${(category.questions?.length || 0) - alreadySentCount - totalCount} from enquiry)`
                                        : ""}
                                    </Text>
                                  </div>
                                </div>

                                <Text
                                  strong
                                  className={allSelected ? "all-selected" : ""}
                                >
                                  {selectedCount}/{totalCount} selected
                                </Text>
                              </div>
                            }
                          >
                            {category.questions?.map((question) => (
                              <div
                                key={question.questionId}
                                className="question-item"
                              >
                                <Checkbox
                                  checked={selectedQuestions[
                                    category.categoryId
                                  ]?.includes(question.questionId)}
                                  disabled={isCheckboxDisabled(
                                    question.questionId,
                                    question.questionTitle,
                                  )}
                                  onChange={() =>
                                    handleCheckboxChange(
                                      category.categoryId,
                                      question.questionId,
                                      question.questionTitle,
                                    )
                                  }
                                />
                                <Text
                                  strong
                                  style={{
                                    color: isMandatoryLocked(question.questionId)
                                      ? "#1677ff"
                                      : isCheckboxDisabled(
                                            question.questionId,
                                            question.questionTitle,
                                          )
                                        ? "#8c8c8c"
                                        : "",
                                  }}
                                >
                                  {question.questionTitle}
                                  {getQuestionStatusSuffix(
                                    question.questionId,
                                    question.questionTitle,
                                  )}
                                </Text>
                              </div>
                            ))}
                          </Panel>
                        );
                      })}
                    </Collapse>
                  </Spin>
                </div>
              ),
            },
            {
              key: "2",
              label: `Threads (${predefinedThreads?.length || 0})`,
              children: (
                <Spin spinning={loading}>
                  <div style={{ marginTop: "10px" }}>
                    {predefinedThreads?.length === 0 ? (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "40px 0",
                          color: "#999",
                        }}
                      >
                        No custom threads available. Create threads under
                        Question Bank Management.
                      </div>
                    ) : (
                      <Collapse
                        expandIconPosition="end"
                        className="question-collapse"
                      >
                        {predefinedThreads?.map((thread) => {
                          const threadQuestions = getThreadQuestionDetails(thread);
                          const threadAlreadySent = isThreadWhollyLocked(thread);
                          const sendableIds =
                            getThreadSendableQuestionIds(thread);
                          const sendableCount = sendableIds.length;
                          const selectedInThread = sendableIds.filter((id) =>
                            selectedThreadQuestionIds.includes(id),
                          );
                          const allSendableSelected =
                            !threadAlreadySent &&
                            sendableCount > 0 &&
                            selectedInThread.length === sendableCount;
                          const someSendableSelected =
                            !threadAlreadySent &&
                            selectedInThread.length > 0 &&
                            !allSendableSelected;
                          const includedLabel = threadAlreadySent
                            ? `${threadQuestions.length} questions included (already sent)`
                            : sendableCount < threadQuestions.length
                              ? `${threadQuestions.length} questions included (${sendableCount} available to send)`
                              : `${threadQuestions.length} questions included`;

                          return (
                            <Panel
                              key={thread.predefinedThreadId}
                              header={
                                <div className="category-header">
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 12,
                                    }}
                                  >
                                    <Checkbox
                                      checked={allSendableSelected}
                                      indeterminate={someSendableSelected}
                                      disabled={
                                        threadAlreadySent || sendableCount === 0
                                      }
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={() => handleThreadSelectAll(thread)}
                                    />
                                    <div>
                                      <Title
                                        level={5}
                                        style={{
                                          marginBottom: 0,
                                          color: threadAlreadySent
                                            ? "#8c8c8c"
                                            : undefined,
                                        }}
                                      >
                                        {thread.threadName}
                                        {threadAlreadySent ? " (already sent)" : ""}
                                      </Title>
                                      <Text type="secondary">
                                        {includedLabel}
                                      </Text>
                                    </div>
                                  </div>
                                  <Text
                                    strong
                                    type={threadAlreadySent ? "secondary" : undefined}
                                    className={
                                      selectedInThread.length > 0
                                        ? "all-selected"
                                        : ""
                                    }
                                  >
                                    {threadAlreadySent
                                      ? "Already sent"
                                      : selectedInThread.length > 0
                                        ? `${selectedInThread.length} selected`
                                        : "Unselected"}
                                  </Text>
                                </div>
                              }
                            >
                              <div style={{ paddingLeft: "24px" }}>
                                <Text
                                  strong
                                  style={{
                                    display: "block",
                                    marginBottom: "8px",
                                    color: "#8c8c8c",
                                  }}
                                >
                                  Questions in this thread:
                                </Text>
                                {threadQuestions.map((question, idx) => {
                                  const alreadySent = isThreadQuestionAlreadySent(
                                    thread,
                                    question.questionId,
                                  );
                                  const isBlocked = isQuestionBlocked(
                                    question.questionId,
                                    question.questionTitle,
                                  );
                                  const isDisabled = alreadySent || isBlocked;
                                  return (
                                    <div
                                      key={question.questionId || idx}
                                      className="question-item"
                                    >
                                      <Checkbox
                                        checked={
                                          !isDisabled &&
                                          selectedThreadQuestionIds.includes(
                                            question.questionId,
                                          )
                                        }
                                        disabled={isDisabled}
                                        onChange={() =>
                                          handleThreadQuestionToggle(
                                            thread,
                                            question.questionId,
                                            question.questionTitle,
                                          )
                                        }
                                      />
                                      <Text
                                        strong={!isDisabled}
                                        type={isDisabled ? "secondary" : undefined}
                                      >
                                        {question.questionTitle}
                                        {alreadySent ? " (already sent)" : ""}
                                        {isBlocked && !alreadySent
                                          ? " (from enquiry — not required)"
                                          : ""}
                                      </Text>
                                    </div>
                                  );
                                })}
                                {threadQuestions.length === 0 && (
                                  <Text type="secondary">
                                    No active questions found in this thread.
                                  </Text>
                                )}
                              </div>
                            </Panel>
                          );
                        })}
                      </Collapse>
                    )}
                  </div>
                </Spin>
              ),
            },
          ]}
        />

        {/* Footer */}
        <div className="question-footer">
          <Text strong>
            {getUniqueSelectedCount()} unique questions selected
          </Text>

          <CustomButton
            type="primary"
            icon={<SendOutlined />}
            loading={sendQuestionnaireLoading}
            disabled={getUniqueSelectedCount() === 0}
            onClick={handleSendQuestionnaire}
          >
            Send Questionnaire
          </CustomButton>
        </div>
      </Card>
      {questionFormModal && (
        <CustomModal
          title="Add New Question"
          open={questionFormModal}
          centered
          closeIcon
          width={MODAL_WIDTHS}
          onCancel={() => setQuestionFormModal(false)}
          content={
            <AddQuestionForm
              onClose={() => setQuestionFormModal(false)}
              orgId={orgId}
              dealId={dealId}
              dealCategoryId={dealCategoryId}
              entityTypeId={entityTypeId}
            />
          }
        />
      )}
    </div>
  );
};

export default SendQuestionnaireForm;
